// lib/amplitude.ts
import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-core';

let initialized = false;
let currentPageTitle: string | null = null;

export const initAmplitude = () => {
  if (!initialized && typeof window !== 'undefined') {
    amplitude.init('a4a0332df33e32254dee641785100c6f', undefined, {
      defaultTracking: false,
    });

    // 기본 User Property 설정
    const identifyObj = new Identify();

    // 브라우저 정보
    identifyObj.set('browser', getBrowserInfo());
    identifyObj.set('browser_version', getBrowserVersion());
    identifyObj.set('os', getOSInfo());
    identifyObj.set('device_type', getDeviceType());
    identifyObj.set('screen_resolution', getScreenResolution());
    identifyObj.set('viewport_size', getViewportSize());
    identifyObj.set('language', navigator.language);
    identifyObj.set(
      'timezone',
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    identifyObj.set('user_agent', navigator.userAgent);

    // 네트워크 정보 (가능한 경우)
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      identifyObj.set('connection_type', connection.effectiveType || 'unknown');
      identifyObj.set('connection_speed', connection.downlink || 'unknown');
    }

    // 기타 브라우저 기능 지원 여부
    identifyObj.set('supports_webgl', isWebGLSupported());
    identifyObj.set('supports_geolocation', 'geolocation' in navigator);
    identifyObj.set('supports_notifications', 'Notification' in window);
    identifyObj.set('supports_service_worker', 'serviceWorker' in navigator);

    amplitude.identify(identifyObj);
    initialized = true;
  }
};

// 브라우저 정보 수집 함수들
const getBrowserInfo = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  if (userAgent.includes('Opera')) return 'Opera';
  return 'Unknown';
};

const getBrowserVersion = (): string => {
  const userAgent = navigator.userAgent;
  const match = userAgent.match(
    /(chrome|firefox|safari|edge|opera)\/?\s*(\d+)/i
  );
  return match ? match[2] : 'Unknown';
};

const getOSInfo = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iOS')) return 'iOS';
  return 'Unknown';
};

const getDeviceType = (): string => {
  const userAgent = navigator.userAgent;
  if (/Mobi|Android/i.test(userAgent)) return 'Mobile';
  if (/Tablet|iPad/i.test(userAgent)) return 'Tablet';
  return 'Desktop';
};

const getScreenResolution = (): string => {
  return `${screen.width}x${screen.height}`;
};

const getViewportSize = (): string => {
  return `${window.innerWidth}x${window.innerHeight}`;
};

const isWebGLSupported = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
};

// (공통) /////////////////////////////
export const logPageView = (pageTitle: string) => {
  if (initialized) {
    // 새 페이지 시작
    currentPageTitle = pageTitle;

    console.log('Amplitude:: Page Viewed:', currentPageTitle);

    // utm 파라미터 체크
    const utmParams = new URLSearchParams(window.location.search);
    const utmSource = utmParams.get('utm_source');
    const utmMedium = utmParams.get('utm_medium');
    const utmCampaign = utmParams.get('utm_campaign');

    if (utmSource || utmMedium || utmCampaign) {
      const utmData = {
        ...(utmSource && { utmSource }),
        ...(utmMedium && { utmMedium }),
        ...(utmCampaign && { utmCampaign }),
      };

      amplitude.track('PageView', {
        'Page Title': pageTitle,
        'UTM Source': utmSource,
        'UTM Medium': utmMedium,
        'UTM Campaign': utmCampaign,
      });

      const identifyObj = new Identify();
      Object.entries(utmData).forEach(([key, value]) => {
        identifyObj.set(key, value);
      });
      amplitude.identify(identifyObj);
    } else {
      amplitude.track('PageView', {
        'Page Title': pageTitle,
      });
    }
  }
};

// #000 Intro Page ///////////////////
export const logClickIntroButton = (type: 'start' | 'parking') => {
  amplitude.track('Click intro_btn', {
    type: type,
  });
};

// #001 Main Page ///////////////////
export const logClickTopBarButton = (type: 'map' | 'parking') => {
  amplitude.track('Click topbar_btn', {
    type: type,
  });
};
export const logClickSpaceButton = (
  type: 'phone' | 'pin',
  id: number,
  title: string,
  category: 'floor' | 'category' | 'search'
) => {
  amplitude.track('Click space_btn', {
    type: type,
    id: id,
    title: title,
    category: category,
  });
};
export const logClickSpaceModalButton = (
  type: 'detail' | 'start' | 'end',
  id: number,
  title: string
) => {
  amplitude.track('Click space_modal_btn', {
    type: type,
    id: id,
    title: title,
  });
};
export const logSearchValue = (value: string) => {
  amplitude.track('Search value', {
    value: value,
  });
};

// #002 Navi Page ///////////////////
export const logActivateNavi = (start: string, end: string) => {
  amplitude.track('Activate navi', {
    start: start,
    end: end,
  });
};
export const logClickNaviButton = (
  type: 'Stair' | 'Escalator' | 'Elevator' | 'Short'
) => {
  amplitude.track('Click navi_btn', {
    type: type,
  });
};

// #003 XR-Navi Page //////////////////
export const logClickMoveButton = () => {
  amplitude.track('Click xr_move_btn', {});
};
export const logArrive = () => {
  amplitude.track('Arrive xr_navi', {});
};

// #004 Parking Page //////////////////
export const logRegisterCar = (spot: string) => {
  amplitude.track('Register car', { spot: spot });
};
export const logClickParkingNaviButton = (type: 'start' | 'end') => {
  amplitude.track('Click parking_navi_btn', { type: type });
};
export const logClickRemoveCarButton = () => {
  amplitude.track('Click remove_car_btn', {});
};
